/**
 * Anzurechnendes Einkommen nach § 11 / § 11b SGB II.
 *
 * WICHTIG: Bemessungsgrundlage ist das **Brutto-Erwerbseinkommen**
 * (§ 11 Abs. 1 Satz 1 SGB II — „Einkünfte in Geld"), nicht der bilanzielle
 * Gewinn. Bei Selbständigen ist das eine eigenständige Größe, die NICHT
 * automatisch aus „Umsatz minus Kosten" abgeleitet werden darf — Privat-
 * entnahmen und steuerliche Korrekturen weichen davon ab.
 *
 * Diese Funktion erwartet daher den bereits ermittelten Brutto-Wert
 * (`bruttoErwerbseinkommenCents`) und tut keine Heuristik. Adapter im
 * Frontend, die mangels echter Buchhaltung mit dem Profit als Näherung
 * arbeiten, MÜSSEN diesen Pfad protokollieren — die formelle Stellungnahme
 * darf nie auf einem geschätzten Wert basieren (siehe
 * `FinancialDataset.bruttoErwerbseinkommenCents`).
 *
 * Erwerbseinkommen durchläuft die § 11b-Freibeträge. Sonstiges Einkommen
 * (Kindergeld, Elterngeld, Unterhalt, Renten) wird als optionaler Parameter
 * OHNE Erwerbstätigen-Freibetrag voll auf den Bedarf angerechnet.
 *
 * Logik (§ 11b SGB II):
 *   1. Vom Brutto-Einkommen wird der Grundfreibetrag (100€) abgezogen.
 *   2. Auf das Brutto-Einkommen werden gestaffelte Freibeträge angewendet:
 *        100,01–520€:    20% bleiben anrechnungsfrei
 *        520,01–1000€:   30% bleiben anrechnungsfrei
 *        1000,01–1200€ (oder 1500€ mit Kind): 10% bleiben anrechnungsfrei
 *   3. Anzurechnendes Erwerbseinkommen = Brutto - Grundfreibetrag - Staffelfreibeträge
 *   4. Anzurechnendes Gesamteinkommen = anzurechnendes Erwerbseinkommen + sonstiges Einkommen
 *
 * Pure Function — alle Werte in Cent.
 */
import type { MoneyCents } from '../schema/brands.js';
import { FREIBETRAG_TIERS, GRUNDFREIBETRAG_CENTS } from './regelbedarfe2026.js';

export interface AnzurechnendesEinkommenInput {
  bruttoErwerbseinkommenCents: MoneyCents;
  hasChildrenInBg: boolean;
  /**
   * Sonstiges anrechenbares Einkommen der BG (Kindergeld, Elterngeld, Unterhalt,
   * Renten). Wird ohne Erwerbstätigen-Freibetrag voll angerechnet. Default 0.
   */
  sonstigesEinkommenCents?: MoneyCents;
}

export interface AnzurechnendesEinkommenBreakdown {
  bruttoCents: MoneyCents;
  grundfreibetragCents: MoneyCents;
  staffelfreibetraegeCents: MoneyCents;
  /** Anrechenbares Erwerbseinkommen nach Freibeträgen. */
  anzurechnendesErwerbseinkommenCents: MoneyCents;
  /** Sonstiges Einkommen (voll angerechnet, ohne Freibetrag). */
  sonstigesEinkommenCents: MoneyCents;
  /** Gesamtes anzurechnendes Einkommen (Erwerb nach Freibeträgen + Sonstiges). */
  anzurechnendesEinkommenCents: MoneyCents;
}

export function computeAnzurechnendesEinkommen(
  input: AnzurechnendesEinkommenInput,
): AnzurechnendesEinkommenBreakdown {
  const brutto = input.bruttoErwerbseinkommenCents as number;
  const sonstiges = Math.max(0, (input.sonstigesEinkommenCents as number | undefined) ?? 0);

  if (brutto <= 0) {
    return {
      bruttoCents: 0 as MoneyCents,
      grundfreibetragCents: 0 as MoneyCents,
      staffelfreibetraegeCents: 0 as MoneyCents,
      anzurechnendesErwerbseinkommenCents: 0 as MoneyCents,
      sonstigesEinkommenCents: sonstiges as MoneyCents,
      anzurechnendesEinkommenCents: sonstiges as MoneyCents,
    };
  }

  const grundfreibetrag = Math.min(brutto, GRUNDFREIBETRAG_CENTS);

  const tier1Lower = grundfreibetrag; // ab 100€
  const tier1Upper = FREIBETRAG_TIERS.TIER_1_UPPER_CENTS;
  const tier2Upper = FREIBETRAG_TIERS.TIER_2_UPPER_CENTS;
  const tier3Upper = input.hasChildrenInBg
    ? FREIBETRAG_TIERS.TIER_3_UPPER_CENTS_WITH_KIDS
    : FREIBETRAG_TIERS.TIER_3_UPPER_CENTS_NO_KIDS;

  const tier1Slice = Math.max(0, Math.min(brutto, tier1Upper) - tier1Lower);
  const tier2Slice = Math.max(0, Math.min(brutto, tier2Upper) - tier1Upper);
  const tier3Slice = Math.max(0, Math.min(brutto, tier3Upper) - tier2Upper);

  const staffelfreibetrag = Math.round(
    tier1Slice * FREIBETRAG_TIERS.TIER_1_PERCENT +
      tier2Slice * FREIBETRAG_TIERS.TIER_2_PERCENT +
      tier3Slice * FREIBETRAG_TIERS.TIER_3_PERCENT,
  );

  const anzurechnenErwerb = Math.max(0, brutto - grundfreibetrag - staffelfreibetrag);

  return {
    bruttoCents: brutto as MoneyCents,
    grundfreibetragCents: grundfreibetrag as MoneyCents,
    staffelfreibetraegeCents: staffelfreibetrag as MoneyCents,
    anzurechnendesErwerbseinkommenCents: anzurechnenErwerb as MoneyCents,
    sonstigesEinkommenCents: sonstiges as MoneyCents,
    anzurechnendesEinkommenCents: (anzurechnenErwerb + sonstiges) as MoneyCents,
  };
}
